/**
 * The hero frame sets, generated by scripts/build-sequences.mjs from the 960 original frames into
 * public/frames/<version>/{hd,desktop,mobile,preview} (sequences.json is that script's output).
 * Each set keeps the frames where the picture moves and lists which original frame each one is,
 * so the scroll timing is the 960-frame timing whatever the set. VITE_*_SEQUENCE_BASE_URL may
 * point a set at a CDN that serves the same files.
 *
 * Three roles: the PREVIEW (one small file, every moment of the animation) is on screen within a
 * second of a first visit; the MOTION set is what scrolling draws (its frames decode fast enough
 * to keep up with any scroll speed); on big screens a REST set (1920 px) replaces the motion frame
 * whenever the scroll pauses, where the eye can see the detail.
 */
import type { FrameBundle } from './frameLoader';
import sets from './sequences.json';

export interface SequenceSource {
  baseUrl: string;
  frameCount: number;
  /** Frames on the original timeline (960) and which of them this set kept, ascending. */
  sourceFrameCount: number;
  sourceFrames: readonly number[];
  prefix: string;
  extension: string;
  padLength: number;
  width: number;
  height: number;
  /** 'landscape' sources are composed differently on phones than a dedicated 'portrait' sequence. */
  orientation: 'landscape' | 'portrait';
  /** The set as a few files, in download order (the preview). */
  bundles?: FrameBundle[];
}

interface GeneratedSet extends Omit<SequenceSource, 'orientation' | 'bundles'> {
  bundles?: { file: string; frames: number[]; offsets: number[][] }[];
}
const env = import.meta.env;

const fromSet = (set: GeneratedSet, baseOverride?: string): SequenceSource => {
  const baseUrl = baseOverride || set.baseUrl;
  return {
    baseUrl,
    frameCount: set.frameCount,
    sourceFrameCount: set.sourceFrameCount,
    sourceFrames: set.sourceFrames,
    prefix: set.prefix,
    extension: set.extension,
    padLength: set.padLength,
    width: set.width,
    height: set.height,
    orientation: set.height > set.width ? 'portrait' : 'landscape',
    ...(set.bundles ? { bundles: set.bundles.map((b) => ({ url: `${baseUrl.replace(/\/$/, '')}/${b.file}`, frames: b.frames, offsets: b.offsets as [number, number][] })) } : {}),
  };
};

/** 1920 px: the frame shown at rest on big and high-DPI screens. */
export const HD_SEQUENCE = fromSet(sets.hd, env.VITE_DESKTOP_SEQUENCE_BASE_URL);
/** 1280 px: what desktops draw while scrolling. */
export const DESKTOP_SEQUENCE = fromSet(sets.desktop, env.VITE_DESKTOP_SEQUENCE_LOWRES_BASE_URL);
/** 768 px, fewer frames: phones and narrow windows. */
export const MOBILE_SEQUENCE = fromSet(sets.mobile, env.VITE_MOBILE_SEQUENCE_BASE_URL);
/** 320 px, under 1 MB for the whole animation in two files: on screen first, drawn until the others arrive. */
export const PREVIEW_SEQUENCE = fromSet(sets.preview);

/** Phones and portrait tablets get the cinematic band layout (HeroSequence). */
export const MOBILE_LAYOUT_QUERY = '(max-width: 767px), (max-aspect-ratio: 6/5)';

export interface SequenceChoice {
  /** Drawn while scrolling. */
  motion: SequenceSource;
  /** Drawn when the scroll pauses, if sharper than `motion` is worth it on this screen. */
  rest: SequenceSource | null;
}

/**
 * The sets for a screen: `renderWidthCssPx` is the width the frame is drawn at (a cover-fitted
 * landscape frame on a tall window is wider than the window).
 */
export function pickSequence(renderWidthCssPx: number, devicePixelRatio: number): SequenceChoice {
  const needed = renderWidthCssPx * Math.min(devicePixelRatio || 1, 2);
  if (needed <= 900) return { motion: MOBILE_SEQUENCE, rest: null };
  if (needed <= 1400) return { motion: DESKTOP_SEQUENCE, rest: null };
  return { motion: DESKTOP_SEQUENCE, rest: HD_SEQUENCE };
}

/** The width the hero frame is drawn at: the band width on mobile, the cover width otherwise. */
export function heroRenderWidth(mobileLayout: boolean, viewportWidth: number, viewportHeight: number): number {
  return mobileLayout ? viewportWidth : Math.max(viewportWidth, (viewportHeight * 16) / 9);
}

/**
 * Decoded-frame budget for the motion set. The loader never holds fewer than its decode window
 * (which grows with scroll speed); measured in Chrome, more than this bought no smoothness.
 */
export const decodedBudgetBytes = (mobileLayout: boolean) => (mobileLayout ? 32 : 64) * 1024 * 1024;
